import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { TransportInsuranceMessages } from '../../components/TransportInsuranceMessages';
import { ContactUsCard } from '@app/app/_widgets/ContactUsCard';
import { useInsurancePackage } from '../setups/useInsurancePackage';
import { TransportPolicyValidity } from '@app/domain/model/eshop/transport/TransportPolicyValidity';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { ChangeEvent } from 'react';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { Button } from '@app/app/_components/Button/Button';
import { Message } from '@app/app/_components/Message/Message';
import { InsurancePackage } from '@app/domain/model/eshop/transport/InsurancePackage';
import { useTranslations } from 'next-intl';
import { useCascoOptions } from '../setups/useCascoOptions';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';

interface Props {
    policyValidity: Nullable<TransportPolicyValidity>;
    premiums: TransportPremiumsResponse[];
    setPremiums: (premiums: TransportPremiumsResponse[]) => void;
    insuranceDuration: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: Nullable<string>;
    isCascoPresent?: boolean;
}

export function ChooseCascoStep({
    policyValidity,
    premiums,
    insuranceDuration,
    onChange,
    value,
    isCascoPresent,
}: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();

    const cascoValidity = policyValidity?.cascoPolicyValidity;
    const mtplValidity = policyValidity?.mtplPolicyValidity;

    const insurancePackage = useInsurancePackage(cascoValidity ?? null, mtplValidity ?? null);

    const premiumOptions = useCascoOptions(premiums, insuranceDuration, parameters);

    const showCompare =
        [
            InsurancePackage.cascoMtplInvalid,
            InsurancePackage.bothValidCascoExpiringSoon,
            InsurancePackage.cascoMtplValidExpiringSoon,
            InsurancePackage.cascoValidExpiringSoonMtplInvalid,
            InsurancePackage.mtplValidExpiringSoonCascoInvalid,
            InsurancePackage.bothValidMtplExpiringSoon,
        ].includes(insurancePackage.status) && insuranceDuration === InsuranceDuration.TwelveMonths;

    return (
        <div className="flex flex-col gap-8">
            <TransportInsuranceMessages
                status={insurancePackage.status}
                insuranceDuration={insuranceDuration}
                cascoValidity={cascoValidity ?? null}
                mtplValidity={mtplValidity ?? null}
                isCascoPresent={isCascoPresent}
            />
            <ChoiceButtonInput
                name="insurancePackageCasco"
                options={premiumOptions}
                onChange={onChange}
                value={value ?? undefined}
            />
            {showCompare ? (
                <Message
                    className="md:flex-row md:items-start"
                    variant="info"
                    addon={
                        <Button
                            href="https://www.compensa.lt/transporto-paketu-palyginimas/"
                            preventDefault
                            variant="outlined"
                            size="tiny"
                            target="_blank"
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('eshop.general.compare')}
                        </Button>
                    }>
                    <div className="flex flex-col gap-2">
                        <div className="font-medium">{t('eshop.general.compareInsurances')}</div>
                        <div className="text-sm">{t('eshop.transport.compareInsurancesMessage')}</div>
                    </div>
                </Message>
            ) : (
                <ContactUsCard />
            )}
        </div>
    );
}
